import React from 'react';
import { FormattedMessage } from 'react-intl';
import type { JobSalarySourceBadgeKind } from '../../../../../utils/job-detail/resolve-job-salary-source-badge-kind';

export interface JobSalarySourceBadgeProps {
  kind: JobSalarySourceBadgeKind;
  className?: string;
}

const VARIANT: Record<
  JobSalarySourceBadgeKind,
  string
> = {
  stated_in_listing:
    'bg-[#C8DDCE]/90 text-[#2A4533] border-[#3B5E45]/35 dark:bg-[#1A2E22] dark:text-[#98CFA8] dark:border-[#98CFA8]/35',
  estimated_benchmark:
    'bg-[#F5EDD0] text-[#6E4F00] border-[#B8860B]/25 dark:bg-[#2A1E08] dark:text-[#D4A820] dark:border-[#D4A820]/25',
  unverified_numeric:
    'bg-[#F5EDD0] text-[#6E4F00] border-[#B8860B]/25 dark:bg-[#2A1E08] dark:text-[#D4A820] dark:border-[#D4A820]/25',
};

/**
 * High-visibility pill: listing quote vs market estimate vs unverified pipeline numbers.
 */
export const JobSalarySourceBadge: React.FC<JobSalarySourceBadgeProps> = ({ kind, className = '' }) => {
  const msg =
    kind === 'stated_in_listing' ? (
      <FormattedMessage
        id="jobDetailPage.salarySourceBadge.fromListing"
        defaultMessage="Listed"
        description="Short label: pay range comes from the job listing text"
      />
    ) : kind === 'estimated_benchmark' ? (
      <FormattedMessage
        id="jobDetailPage.salarySourceBadge.estimated"
        defaultMessage="Estimated"
        description="Short label: salary band is a market estimate"
      />
    ) : (
      <FormattedMessage
        id="jobDetailPage.salarySourceBadge.unverified"
        defaultMessage="Unverified"
        description="Short label: numeric pay without a verbatim listing quote"
      />
    );

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold leading-snug normal-case ${VARIANT[kind]} ${className}`.trim()}
    >
      {msg}
    </span>
  );
};
